import {
  ButtonToggle,
  ButtonToggleGroup,
  Col,
  Row,
  Skeleton,
  Icon,
  Text,
} from '@cubeyou-team/hx-react-components';
import React, { useMemo, FC } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import classnames from 'classnames';

import {
  DATA_SOURCE_IDS,
  DATA_SOURCE_COLOR_RGB_MAP,
} from '@discover-frontend/constants';
import { isGenericUs } from '@discover-frontend/utils';

import { getCurrentProductDataSources } from 'src/selectors/myCompany';
import {
  QUERY_BUILDER_TYPES,
  QueryBuilderConstants,
} from 'src/modules/query-builder/constants';
import {
  TRIAL_FEATURES,
  useTrialPaywall,
} from 'src/modules/trial-paywall';
import { isTrialUser } from 'src/utils/AuthHelperMethods';

export const QUERY_BUILDER_DATA_SOURCES = [
  DATA_SOURCE_IDS.TWITTER,
  DATA_SOURCE_IDS.INSTAGRAM,
  DATA_SOURCE_IDS.INSTAGRAM_FOLLOWS,
  DATA_SOURCE_IDS.APPS,
  DATA_SOURCE_IDS.BROWSER,
];

const StyledButtonToggleGroup = styled(ButtonToggleGroup)`
  button {
    border-color: black !important;
  }

  ${Object.entries(DATA_SOURCE_COLOR_RGB_MAP)
    .map(
      ([sourceId, color]) =>
        `& .bg-custom-${sourceId} { background-color: ${color} !important; }`
    )
    .join('\n')}
`;

interface QuerySourceProps {
  onChangeDataSource: (dataSource: string) => void;
}

export const QuerySource: FC<QuerySourceProps> = ({ onChangeDataSource }) => {
  const { guardFeature } = useTrialPaywall();
  const queryBuilder = useSelector((state: any) => state.queryBuilder);
  const dataSource = useSelector((state: any) => state.queryBuilder.dataSource);
  const productDataSources = useSelector((state) =>
    getCurrentProductDataSources(state)
  );
  const conversation = useSelector((state: any) => state.conversation);
  const createAudienceModal = useSelector(
    (state: any) => state.createAudienceModal
  );

  const { queryBuilderType, isCompareQuery } = queryBuilder;
  const isQueryLoading =
    queryBuilder.isLoading ||
    conversation.isLoading ||
    createAudienceModal.isLoading;
  const selectedSource = useMemo(
    () => QueryBuilderConstants.dataSources.find((d) => d.id === dataSource),
    [dataSource]
  );

  const hasTrialButtonOverride = (id: string) => {
    // For trial users on US we want to show the apps and browsing buttons to encourage them to upgrade
    return isTrialUser() && isGenericUs() && (id === DATA_SOURCE_IDS.APPS || id === DATA_SOURCE_IDS.BROWSER);
  }
  const handleChangeDataSource = (nextSource: string) => {
    if (nextSource === DATA_SOURCE_IDS.TWITTER) {
      onChangeDataSource(nextSource);
      return;
    }

    guardFeature(TRIAL_FEATURES.DATA_SOURCE, () => {
      onChangeDataSource(nextSource);
    });
  };

  if (isQueryLoading) {
    return (
      <Col span="auto">
        <Row valign="middle">
          <Col span="auto">
            <Skeleton speed={2} width={150} height={24} viewBox="0 0 250 24">
              <rect x="0" y="0" rx="50" ry="50" width="24" height="24" />
              <rect x="40" y="3" rx="4" ry="4" width="200" height="18" />
            </Skeleton>
          </Col>
        </Row>
      </Col>
    );
  }

  if (queryBuilderType === QUERY_BUILDER_TYPES.CONVERSATION || isCompareQuery)
    return null;

  const sources = QueryBuilderConstants.dataSources
    .filter(
      ({ id }) =>
        id === DATA_SOURCE_IDS.TWITTER || productDataSources.includes(id) || hasTrialButtonOverride(id)
    )
    .sort(
      (a, b) =>
        (QUERY_BUILDER_DATA_SOURCES as string[]).indexOf(a.id) -
        (QUERY_BUILDER_DATA_SOURCES as string[]).indexOf(b.id)
    )
    .map(({ id, value, icon, label }) => ({
      id,
      icon,
      label,
      value,
      isActive: selectedSource.id === id,
    }));

  return (
    <StyledButtonToggleGroup
      selected={dataSource}
      onChange={handleChangeDataSource}
    >
      {sources.map((source) => (
        <ButtonToggle
          key={`source-${source.id}`}
          value={source.id}
          className={classnames('text-white', {
            [`bg-custom-${source.id}`]: source.isActive,
          })}
        >
          <div className="flex items-center gap-2 px-2">
            <Icon
              icon={source.icon}
              size="small"
              variant={source.isActive ? 'white' : 'dark'}
            />
            <Text size="xbody" variant={source.isActive ? 'white' : 'dark'}>
              {source.label}
            </Text>
          </div>
        </ButtonToggle>
      ))}
    </StyledButtonToggleGroup>
  );
};
